/**
 * InAppDescriptionPanel — inline editor for a tab-level "in-app
 * description" (the general_settings text shown at the top of a content
 * tab in the mobile app).
 *
 * Replaces the "Update description" header-button + modal flow on the
 * content list screens (Daily Inspirations, Challenges, Quick Taps,
 * Featured Quick Taps). Per CMS feedback the description should be
 * visible and editable at the top of each content screen — like the v1
 * CMS — not hidden behind a button (Monday #12171251310).
 *
 * The parent owns fetching and saving (each screen wires a different
 * action / store slice); the panel owns the draft + dirty state:
 *
 *   <InAppDescriptionPanel
 *     tabName="Challenges"
 *     value={stored}
 *     onSave={(v) => dispatch(updateChallengeIntro(v))}
 *   />
 *
 * onSave should return a promise (all four screens return the dispatch
 * result): while it is pending the panel shows "Saving…" and disables
 * both buttons, so Discard can't race an in-flight PUT.
 *
 * While pristine (no edits yet) the draft follows the stored value, so
 * the on-mount fetch lands in the textarea. The first keystroke pins the
 * draft: later stored changes — including a stale mount fetch resolving
 * after a save — can no longer replace what the admin typed or saved.
 * Discard returns to pristine (follows the store again).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from './Button';

export function InAppDescriptionPanel({ tabName, value, onSave }) {
  const stored = value ?? '';
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);
  const pristineRef = useRef(true);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (pristineRef.current) setDraft(stored);
  }, [stored]);

  const dirty = draft !== stored;

  const handleSave = () => {
    const result = onSave?.(draft);
    if (result && typeof result.then === 'function') {
      setSaving(true);
      result.finally(() => {
        if (mountedRef.current) setSaving(false);
      });
    }
  };

  return (
    <div className="bg-bg relative" style={{ padding: '0 28px 16px' }}>
      {/* Same eyebrow recipe as PageHeader so the panel reads as part
       *  of the header block it sits under. */}
      <div
        className="font-condensed font-bold uppercase text-teal-green-dark"
        style={{ fontSize: 11, letterSpacing: '0.12em', marginBottom: 6 }}
      >
        In-app description
      </div>

      <textarea
        value={draft}
        onChange={(e) => {
          pristineRef.current = false;
          setDraft(e.target.value);
        }}
        aria-label="In-app description"
        className="w-full bg-cream border border-sand text-navy focus:bg-surface focus:border-teal-green"
        style={{
          maxWidth: 720,
          minHeight: 64,
          padding: '10px 14px',
          borderRadius: 8,
          fontSize: 14,
          lineHeight: 1.45,
          resize: 'vertical',
          outline: 'none',
          fontFamily: 'inherit',
          boxShadow: 'none',
          display: 'block',
        }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = '0 0 0 3px #75A29318';
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = 'none';
        }}
      />

      <div
        className="flex items-center"
        style={{ gap: 8, marginTop: 8, maxWidth: 720 }}
      >
        <span
          className="text-text-light mr-auto"
          style={{ fontSize: 12, lineHeight: 1.5 }}
        >
          Shown at the top of the{' '}
          <strong className="text-text font-semibold">{tabName}</strong> tab in
          the mobile app.
        </span>
        <span
          className="text-text-muted font-mono"
          style={{ fontSize: 11.5 }}
        >
          {draft.length} chars
        </span>
        {dirty && !saving && (
          <Button
            variant="ghost"
            onClick={() => {
              pristineRef.current = true;
              setDraft(stored);
            }}
          >
            Discard
          </Button>
        )}
        <Button
          variant="primary"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save description'}
        </Button>
      </div>
    </div>
  );
}

export default InAppDescriptionPanel;
